import React, { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Button, Dialog } from "iuvity-ui-components";
import { CustomInput } from "../inputs/CustomInput";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { WidgetCreatorSchema } from "../../validations/schemas";
import { DialogProps } from "../dialog/CustomDialog";
import { AppContext } from "../../context";

interface WidgetCreatorProps {
  visible: boolean;
  setModalProps: React.Dispatch<
    React.SetStateAction<{
      visible: boolean;
      type: "CREATE" | "UPDATE" | null;
    }>
  >;
  update: boolean;
  createFn: Function;
  reloadFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
  options: any;
}

export const widgetCreatorDefaultValues = {
  widgetname: "",
  releaseversion: "",
  description: "",
};

export const WidgetCreator: React.FC<WidgetCreatorProps> = ({
  visible,
  setModalProps,
  update,
  createFn,
  reloadFn,
  auxModalController,
  options,
}) => {
  const {
    t,
    appState: { user, selectedWidget },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    getValues,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const onHideModal = () => {
    setModalProps((prev) => ({
      ...prev,
      visible: false,
    }));
    reset(widgetCreatorDefaultValues);
  };

  const onCreateWidget = async () => {
    const formValues = getValues();
    onHideModal();
    const actionStatus = await createFn(update ? "UPDATE" : "CREATE", [
      {
        ...formValues,
        [update ? "updatedby" : "createdby"]: user?.name,
      },
    ]);
    if (actionStatus === "SUCCESS") {
      reloadFn();
    }
  };

  const handleModalProps = () => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t(`Widgets.${update ? "save" : "create"}ModalTitle`),
      content: t(`Widgets.${update ? "save" : "create"}ModalContent`),
      onConfirm: onCreateWidget,
    }));
  };

  useEffect(() => {
    if (update && selectedWidget) {
      reset({
        id: selectedWidget.id,
        widgetname: `${selectedWidget.widgetname}`,
        releaseversion: selectedWidget?.releaseversion,
        description: selectedWidget?.description,
      });
    } else {
      reset(widgetCreatorDefaultValues);
    }
  }, [reset, selectedWidget, setAppState, update]);

  const renderFooter = () => {
    return (
      <div className="flex align-items-center justify-content-flex-end">
        <Button
          label={t("Widgets.cancelBtnLabel")}
          onClick={onHideModal}
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={
            update ? t("Widgets.saveBtnLabel") : t("Widgets.createBtnLabel")
          }
          variant="p-button"
          onClick={handleModalProps}
          disabled={update ? !(isValid && isDirty) : !isValid}
        />
      </div>
    );
  };

  return (
    <Dialog
      className="dialog-form"
      header={
        update ? t("Widgets.editWidgetTitle") : t("Widgets.newWidgetTitle")
      }
      visible={visible}
      onHide={onHideModal}
      style={{ width: "25rem" }}
      footer={renderFooter}
    >
      <div className="form sm">
        <div key="widgetname">
          <Controller
            key="widgetname"
            name="widgetname"
            control={control}
            rules={(WidgetCreatorSchema(t) as any).widgetname}
            render={({ field }) => {
              return (
                <CustomInput
                  id={`input-widgetname`}
                  className={
                    // @ts-ignore
                    errors["widgetname"]?.message ? "p-invalid" : "mb-3"
                  }
                  label={t(`Widgets.widgetnameLabel`)}
                  placeholder={t(`Widgets.widgetnamePlaceholderLabel`)}
                  type="text"
                  autoComplete="off"
                  keyfilter={
                    (WidgetCreatorSchema(t) as any)["widgetname"]?.keyfilter
                  }
                  maxLength={(WidgetCreatorSchema(t) as any)["widgetname"]?.maxLength?.value}
                  {...field}
                />
              );
            }}
          />
          <ErrorMessage
            errors={errors}
            name="widgetname"
            //@ts-ignore
            render={({ message }) => (
              <small
                className="p-error block"
                style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
              >
                {message}
              </small>
            )}
          />
        </div>

        <div key="description">
          <Controller
            key="description"
            name="description"
            control={control}
            rules={
              update && !selectedWidget?.createdby
                ? (WidgetCreatorSchema(t) as any)["descriptionvs2"]
                : (WidgetCreatorSchema(t) as any)["description"]
            }
            render={({ field }) => {
              return (
                <CustomInput
                  id={`input-description`}
                  className={
                    // @ts-ignore
                    errors["description"]?.message ? "p-invalid" : "mb-3"
                  }
                  label={t(`Widgets.descriptionLabel`)}
                  placeholder={t(`Widgets.descriptionPlaceholderLabel`)}
                  type="text"
                  autoComplete="off"
                  keyfilter={
                    (WidgetCreatorSchema(t) as any)["description"]?.keyfilter
                  }
                  maxLength={(WidgetCreatorSchema(t) as any)["description"]?.maxLength?.value}
                  {...field}
                />
              );
            }}
          />
          <ErrorMessage
            errors={errors}
            name="description"
            //@ts-ignore
            render={({ message }) => (
              <small
                className="p-error block"
                style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
              >
                {message}
              </small>
            )}
          />
        </div>

        <div key="releaseversion">
          <Controller
            key="releaseversion"
            name="releaseversion"
            control={control}
            rules={(WidgetCreatorSchema(t) as any).releaseversion}
            render={({ field }) => {
              return (
                // @ts-ignore
                <Dropdown
                  id="im-type-dropdown"
                  className="dropdown mb-3"
                  label={t("Widgets.releaseversionLabel")}
                  placeholder={t("Widgets.releaseversionPlaceholder")}
                  options={options.sort((a: any, b: any) =>
                    b.label.localeCompare(a.label)
                  )}
                  filter={false}
                  showClear={false}
                  style={{
                    zIndex: "2300 !important",
                  }}
                  {...field}
                />
              );
            }}
          />
          <ErrorMessage
            errors={errors}
            name="widgetname"
            //@ts-ignore
            render={({ message }) => (
              <small
                className="p-error block"
                style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
              >
                {message}
              </small>
            )}
          />
        </div>
      </div>
    </Dialog>
  );
};
